import { captureException } from '@sentry/react-router'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import createSafeLoginRedirect from 'ui-lib/createSafeLoginRedirect'
import { isTransientRequestError } from 'ui-lib/setupCabinetClient'

import type { ApiResponseError } from '~/api/types.gen'

// --- CONSTANTS ---
const IGNORED_STATUS = new Set([404, 451, 999])
const IGNORED_ERRORS = new Set(['limit_avance', 'unauthorized'])
const AUTH_ERRORS = new Set(['expired_token', 'unauthorized'])

/* v8 ignore start */
const handleSentryLog = (error: ApiResponseError, context: 'mutation' | 'query', hash?: string) => {
	const isIgnored = IGNORED_STATUS.has(error.status_code) || IGNORED_ERRORS.has(error.code) || isTransientRequestError(error)

	if (isIgnored) return

	// cause — окремо від extra: у Sentry він і так видимий через ланцюжок linkedErrors.
	const { cause, ...errorData } = error as ApiResponseError & { cause?: unknown }
	// details?.length (не details) — порожній масив давав би Error('').
	const message = error.details?.length ? error.details.join(', ') : error.message
	const sanitizedHash = hash?.replaceAll(/\d+/g, '')

	// cause — оригінальна кинута помилка (interceptor кладе її в CabinetApiError.cause):
	// без неї Sentry бачить лише стек цього wrapper-а і склеює всі різні збої в один issue.
	// fingerprint розводить їх примусово: code + status + message без цифр — кардинальність
	// обмежена (динаміка details з ідентифікаторами не плодить окремі issues).
	captureException(new Error(message, { cause: cause ?? error }), {
		extra: { ...errorData, context },
		fingerprint: ['cabinet-api-error', error.code, String(error.status_code), error.message.replaceAll(/\d+/g, '')],
		tags: sanitizedHash ? { queryHash: sanitizedHash } : { context },
	})
}

const UTMQueryClientContainer = ({ children }: { readonly children: ReactNode }) => {
	const navigate = useNavigate()
	const location = useLocation()

	const navRef = useRef({ location, navigate })

	useEffect(() => {
		navRef.current = { location, navigate }
	}, [navigate, location])

	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						gcTime: 5000,
						refetchOnMount: false,
						refetchOnReconnect: true,
						refetchOnWindowFocus: true,
						retry: false,
						staleTime: 10_000,
					},
				},
				mutationCache: new MutationCache({
					onError: (error) => {
						handleSentryLog(error, 'mutation')
					},
				}),
				queryCache: new QueryCache({
					onError: (error, query) => {
						handleSentryLog(error, 'query', query.queryHash)
					},
				}),
			}),
	)

	useEffect(() => {
		const handleAuthAction = (error: ApiResponseError) => {
			if (!AUTH_ERRORS.has(error.code)) return
			// Згенерований ключ — масив з одного обʼєкта ({ _id, baseUrl, tags }), тож плаский
			// ['active-session'] не матчився і інвалідація була no-op.
			void queryClient.invalidateQueries({ queryKey: [{ tags: ['active-session'] }] })
			const { location: l, navigate: n } = navRef.current
			createSafeLoginRedirect(n, l)()
		}

		const unsubQuery = queryClient.getQueryCache().subscribe((event) => {
			if (event.type === 'updated' && event.action.type === 'error') {
				handleAuthAction(event.action.error as ApiResponseError)
			}
		})

		const unsubMutation = queryClient.getMutationCache().subscribe((event) => {
			if (event.type === 'updated' && event.action.type === 'error') {
				handleAuthAction(event.action.error as ApiResponseError)
			}
		})

		return () => {
			unsubQuery()
			unsubMutation()
		}
	}, [queryClient])

	return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
/* v8 ignore stop */

export default UTMQueryClientContainer
